import React, { useState } from 'react';
import { Eye, EyeOff, Info, Shield } from 'lucide-react';
import Input from '../ui/Input';
import Button from '../ui/Button';
import { ConnectorCatalogEntry, ConnectorSetupField } from '../../types';

interface ConnectorSetupFormProps {
  connector: ConnectorCatalogEntry;
  /** Installing demands a complete setup; reconfiguring merges onto what is already stored. */
  mode: 'install' | 'reconfigure';
  /** Current non-secret values, when reconfiguring. Secrets are never readable, so never prefilled. */
  currentConfig?: Record<string, string>;
  onSubmit: (values: { config: Record<string, string>; secrets: Record<string, string> }) => Promise<void>;
  isSaving: boolean;
  /** Field-level messages returned by the backend, keyed as `config.baseUrl` / `secrets.apiKey`. */
  fieldErrors?: Record<string, string>;
}

/**
 * The setup form, GENERATED from the connector's declared fields.
 *
 * Nothing here knows what WooCommerce or Stripe ask for — the backend's connector descriptor does,
 * and it arrives on the catalog entry. A vendor added to a capability pack becomes installable
 * without a line changing in this file, which is the whole point of the connector model.
 *
 * `config` and `credential` are rendered differently because they ARE different: config is durable
 * and readable, credentials are write-only and sealed at rest. No response ever returns a secret,
 * so reconfiguring shows blank credential inputs and leaving them blank keeps what is stored.
 */
const ConnectorSetupForm: React.FC<ConnectorSetupFormProps> = ({
  connector,
  mode,
  currentConfig,
  onSubmit,
  isSaving,
  fieldErrors = {},
}) => {
  const [config, setConfig] = useState<Record<string, string>>(() => ({ ...(currentConfig ?? {}) }));
  const [secrets, setSecrets] = useState<Record<string, string>>({});
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({});

  const isReconfigure = mode === 'reconfigure';

  const validate = (): boolean => {
    const errors: Record<string, string> = {};

    for (const field of connector.config) {
      const value = (config[field.name] ?? '').trim();
      if (field.required && !value) {
        errors[`config.${field.name}`] = `${field.label} is required`;
      } else if (value && field.format === 'url' && !isHttpUrl(value)) {
        errors[`config.${field.name}`] = 'Enter a full URL, e.g. https://your-store.com';
      }
    }

    // On reconfigure a blank credential means "keep the stored one", so it is only required on install.
    if (!isReconfigure) {
      for (const field of connector.credential) {
        if (field.required && !(secrets[field.name] ?? '').trim()) {
          errors[`secrets.${field.name}`] = `${field.label} is required`;
        }
      }
    }

    setLocalErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validate()) return;

    const suppliedSecrets: Record<string, string> = {};
    for (const name of Object.keys(secrets)) {
      const value = (secrets[name] ?? '').trim();
      if (value.length > 0) suppliedSecrets[name] = value;
    }

    await onSubmit({ config: trimmed(config), secrets: suppliedSecrets });
  };

  const errorFor = (scope: string, name: string): string | undefined =>
    localErrors[`${scope}.${name}`] ?? fieldErrors[`${scope}.${name}`];

  const renderField = (field: ConnectorSetupField, scope: 'config' | 'secrets') => {
    const isSecret = scope === 'secrets';
    const value = isSecret ? (secrets[field.name] ?? '') : (config[field.name] ?? '');
    const error = errorFor(scope, field.name);
    const setValue = (next: string) => {
      const update = (prev: Record<string, string>) => ({ ...prev, [field.name]: next });
      if (isSecret) setSecrets(update);
      else setConfig(update);
      if (error) setLocalErrors(prev => ({ ...prev, [`${scope}.${field.name}`]: '' }));
    };

    return (
      <div key={`${scope}.${field.name}`} className="space-y-1">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {field.label}
          {field.required && !isReconfigure && <span className="text-red-500 ml-1">*</span>}
        </label>
        <div className="relative">
          <Input
            type={isSecret && !revealed[field.name] ? 'password' : 'text'}
            value={value}
            onChange={event => setValue(event.target.value)}
            placeholder={
              isSecret && isReconfigure ? 'Leave blank to keep the current value' : field.help ?? ''
            }
            autoComplete={isSecret ? 'new-password' : 'off'}
            className={error ? 'border-red-500' : ''}
          />
          {isSecret && value.length > 0 && (
            <button
              type="button"
              onClick={() => setRevealed(prev => ({ ...prev, [field.name]: !prev[field.name] }))}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              title={revealed[field.name] ? 'Hide' : 'Show'}
            >
              {revealed[field.name] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          )}
        </div>
        {error ? (
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        ) : (
          field.help && !isSecret && (
            <p className="text-xs text-gray-500 dark:text-gray-400">{field.help}</p>
          )
        )}
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold dark:text-white text-gray-900">
          {isReconfigure ? `Configure ${connector.label}` : `Set up ${connector.label}`}
        </h3>
        {connector.description && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{connector.description}</p>
        )}
      </div>

      {connector.unlocks.length > 0 && (
        <div className="flex gap-3 p-3 rounded-lg bg-primary/5 border border-primary/20">
          <Info className="w-4 h-4 text-primary shrink-0 mt-0.5" />
          <div className="text-sm text-gray-700 dark:text-gray-300">
            <span className="font-medium">Enables:</span>{' '}
            {connector.unlocks.map(capability => capability.split('.').pop()).join(', ')}
          </div>
        </div>
      )}

      {connector.config.length > 0 && (
        <div className="space-y-4">{connector.config.map(field => renderField(field, 'config'))}</div>
      )}

      {connector.credential.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 pt-2">
            <Shield className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Credentials</span>
          </div>
          {connector.credential.map(field => renderField(field, 'secrets'))}
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Encrypted before storage. They are never shown again{isReconfigure && ', so leaving a field blank keeps the stored value'}.
          </p>
        </div>
      )}

      <div className="flex justify-end gap-3 pt-2">
        <Button type="submit" isLoading={isSaving}>
          {isReconfigure ? 'Save changes' : `Install ${connector.label}`}
        </Button>
      </div>
    </form>
  );
};

function trimmed(values: Record<string, string>): Record<string, string> {
  const result: Record<string, string> = {};
  for (const key of Object.keys(values)) {
    const clean = (values[key] ?? '').trim();
    if (clean.length > 0) result[key] = clean;
  }
  return result;
}

function isHttpUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

export default ConnectorSetupForm;
